import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation, type Language } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import LaneStatusPill from "../lanes/LaneStatusPill";
import { STATUS_SLUGS, type StatusSlug, type StatusCategory, type StatusFlags } from "../lanes/laneStatus";
import {
    type StatusConfig,
    type StatusConfigPatch,
    fetchStatusConfig,
    mergeOverrides,
    applyEffectiveConfig,
    allStatusConfigs,
    patchStatusConfig,
    resetStatusConfig,
    resetAllStatusConfig,
    defaultStatusConfig,
} from "../lanes/laneStatusConfig";

// Settings UI for the Lanes status registry. One card per status; admins
// edit the display label (English + optional Polish), the KPI category,
// which fields the status carries (totes / auctions / auction end date),
// and the pill's primary colour. Everything persists to the BE
// (/admin/lane-statuses) and re-applies to the live registry so the rest
// of the Lanes module rethemes/relabels in place. Resetting a status
// drops its override row and restores the FE default.

const CATEGORY_OPTIONS: { value: StatusCategory; key: string }[] = [
    { value: "auction", key: "lanes_cfg_cat_auction" },
    { value: "outbound", key: "lanes_cfg_cat_outbound" },
    { value: "stock", key: "lanes_cfg_cat_stock" },
    { value: "idle", key: "lanes_cfg_cat_idle" },
    { value: "unprocessed", key: "lanes_cfg_cat_unprocessed" },
    { value: "none", key: "lanes_cfg_cat_none" },
];

// Snapshot the currently-applied registry into an editable record. Used
// as the initial state so the screen shows the cached/default values
// before the BE fetch resolves (no empty flash).
const snapshotRegistry = (): Record<StatusSlug, StatusConfig> => {
    const out = {} as Record<StatusSlug, StatusConfig>;
    for (const c of allStatusConfigs()) {
        out[c.slug] = {
            bar_colour: c.bar_colour,
            label_en: c.label_en,
            label_pl: c.label_pl,
            flags: { ...c.flags },
            category: c.category,
        };
    }
    return out;
};

const labelFor = (config: StatusConfig, language: Language): string =>
    language === "pl" && config.label_pl && config.label_pl.trim() !== ""
        ? config.label_pl
        : config.label_en;

const LaneStatusColoursAdmin = () => {
    const { t, language } = useTranslation();
    const readOnly = useReadOnly();
    const [configs, setConfigs] = useState<Record<StatusSlug, StatusConfig>>(snapshotRegistry);
    const [customised, setCustomised] = useState<Set<string>>(new Set());
    const [pendingSlug, setPendingSlug] = useState<StatusSlug | null>(null);
    const [pendingReset, setPendingReset] = useState(false);

    // Pull the server-of-record on mount so the page always edits current
    // state, even if the localStorage cache is stale. A boot fetch already
    // runs from App.tsx; this is a per-mount refresh.
    useEffect(() => {
        fetchStatusConfig()
            .then(rows => {
                const merged = mergeOverrides(rows);
                applyEffectiveConfig(merged);
                setConfigs(merged);
                setCustomised(new Set(rows.map(r => r.slug)));
            })
            .catch(() => { /* keep snapshot; suppress mount-time toast noise */ });
    }, []);

    // Optimistically apply a single-status change locally + to the live
    // registry, then PATCH. On failure roll back to the pre-edit state.
    const commit = async (slug: StatusSlug, patch: StatusConfigPatch, next: StatusConfig) => {
        if (readOnly) return;
        const prevConfigs = configs;
        const prevCustom = customised;
        const nextConfigs = { ...configs, [slug]: next };
        setConfigs(nextConfigs);
        applyEffectiveConfig(nextConfigs);
        setCustomised(prev => new Set(prev).add(slug));
        setPendingSlug(slug);
        try {
            await patchStatusConfig(slug, patch);
        } catch {
            toast.error(t("server_error"), { toastId: "lane-cfg-save-err" });
            setConfigs(prevConfigs);
            applyEffectiveConfig(prevConfigs);
            setCustomised(prevCustom);
        } finally {
            setPendingSlug(null);
        }
    };

    const reset = async (slug: StatusSlug) => {
        if (readOnly) return;
        const prevConfigs = configs;
        const prevCustom = customised;
        const next = defaultStatusConfig(slug);
        const nextConfigs = { ...configs, [slug]: next };
        setConfigs(nextConfigs);
        applyEffectiveConfig(nextConfigs);
        setCustomised(prev => { const s = new Set(prev); s.delete(slug); return s; });
        setPendingSlug(slug);
        try {
            await resetStatusConfig(slug);
        } catch {
            toast.error(t("server_error"), { toastId: "lane-cfg-save-err" });
            setConfigs(prevConfigs);
            applyEffectiveConfig(prevConfigs);
            setCustomised(prevCustom);
        } finally {
            setPendingSlug(null);
        }
    };

    const resetAll = async () => {
        if (readOnly) return;
        const prevConfigs = configs;
        const prevCustom = customised;
        const defaults = {} as Record<StatusSlug, StatusConfig>;
        for (const slug of STATUS_SLUGS) defaults[slug] = defaultStatusConfig(slug);
        setConfigs(defaults);
        applyEffectiveConfig(defaults);
        setCustomised(new Set());
        setPendingReset(true);
        try {
            await resetAllStatusConfig();
        } catch {
            toast.error(t("server_error"), { toastId: "lane-cfg-save-err" });
            setConfigs(prevConfigs);
            applyEffectiveConfig(prevConfigs);
            setCustomised(prevCustom);
        } finally {
            setPendingReset(false);
        }
    };

    const customisedCount = customised.size;

    // Render alphabetically by the displayed label (active language) so
    // admins can scan A-Z rather than the source slug order.
    const sortedSlugs = [...STATUS_SLUGS].sort((a, b) =>
        labelFor(configs[a], language).localeCompare(labelFor(configs[b], language)));

    return (
        <div className="lane-colours">
            <p className="admin-settings-section-hint">{t("lanes_cfg_hint")}</p>
            <div className="lane-colours-toolbar">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={resetAll}
                    disabled={pendingReset || customisedCount === 0 || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <RotateCcw size={14} strokeWidth={1.8} style={{ marginRight: 6, verticalAlign: -2 }} />
                    {t("lanes_colours_reset_all")}
                </button>
            </div>
            <div className="lane-cfg-list">
                {sortedSlugs.map(slug => (
                    <StatusRowEditor
                        key={slug}
                        slug={slug}
                        config={configs[slug]}
                        isCustom={customised.has(slug)}
                        disabled={pendingSlug === slug || pendingReset || readOnly}
                        language={language}
                        t={t}
                        onCommit={commit}
                        onReset={reset}
                    />
                ))}
            </div>
        </div>
    );
};

interface StatusRowEditorProps {
    slug: StatusSlug;
    config: StatusConfig;
    isCustom: boolean;
    disabled: boolean;
    language: Language;
    t: (key: string) => string;
    onCommit: (slug: StatusSlug, patch: StatusConfigPatch, next: StatusConfig) => void;
    onReset: (slug: StatusSlug) => void;
}

const StatusRowEditor = ({ slug, config, isCustom, disabled, language, t, onCommit, onReset }: StatusRowEditorProps) => {
    // Text inputs are committed on blur (not per keystroke) — hold a local
    // draft and resync if the backing config changes (e.g. after a reset).
    const [labelEn, setLabelEn] = useState(config.label_en);
    const [labelPl, setLabelPl] = useState(config.label_pl ?? "");

    useEffect(() => {
        setLabelEn(config.label_en);
        setLabelPl(config.label_pl ?? "");
    }, [config.label_en, config.label_pl]);

    const commitLabelEn = () => {
        const v = labelEn.trim();
        if (v === config.label_en) return;
        // Empty reverts to the FE default English label.
        const effective = v === "" ? defaultStatusConfig(slug).label_en : v;
        setLabelEn(effective);
        onCommit(slug, { label_en: v === "" ? null : v }, { ...config, label_en: effective });
    };

    const commitLabelPl = () => {
        const v = labelPl.trim();
        if (v === (config.label_pl ?? "")) return;
        // Empty Polish label falls back to English at display time.
        onCommit(slug, { label_pl: v === "" ? null : v }, { ...config, label_pl: v === "" ? null : v });
    };

    const setColour = (hex: string) => {
        const upper = hex.toUpperCase();
        onCommit(slug, { bar_colour: upper }, { ...config, bar_colour: upper });
    };

    const setCategory = (value: StatusCategory) => {
        onCommit(slug, { category: value }, { ...config, category: value });
    };

    const toggleFlag = (key: keyof StatusFlags, patchKey: keyof StatusConfigPatch) => {
        const nextValue = !config.flags[key];
        const nextFlags: StatusFlags = { ...config.flags, [key]: nextValue };
        onCommit(slug, { [patchKey]: nextValue }, { ...config, flags: nextFlags });
    };

    return (
        <div className="lane-cfg-row">
            <div className="lane-cfg-row-head">
                <span className="lane-cfg-pill">
                    <LaneStatusPill status={slug} />
                </span>
                <div className="lane-cfg-colour">
                    <input
                        type="color"
                        className="lane-colours-picker"
                        value={config.bar_colour}
                        onChange={e => setColour(e.target.value)}
                        disabled={disabled}
                        aria-label={`${labelFor(config, language)} — ${t("lanes_colours_col_colour")}`}
                    />
                    <span className="lane-colours-hex">{config.bar_colour.toUpperCase()}</span>
                </div>
                <span className="lane-cfg-reset-slot">
                    {isCustom && (
                        <button
                            type="button"
                            className="lane-colours-reset-btn"
                            onClick={() => onReset(slug)}
                            disabled={disabled}
                        >
                            {t("lanes_colours_reset")}
                        </button>
                    )}
                </span>
            </div>

            <div className="lane-cfg-fields">
                <label className="lane-cfg-field">
                    <span className="lane-cfg-field-label">{t("lanes_cfg_label_en")}</span>
                    <input
                        type="text"
                        className="lane-cfg-input"
                        value={labelEn}
                        maxLength={120}
                        onChange={e => setLabelEn(e.target.value)}
                        onBlur={commitLabelEn}
                        disabled={disabled}
                    />
                </label>
                <label className="lane-cfg-field">
                    <span className="lane-cfg-field-label">{t("lanes_cfg_label_pl")}</span>
                    <input
                        type="text"
                        className="lane-cfg-input"
                        value={labelPl}
                        maxLength={120}
                        placeholder={config.label_en}
                        onChange={e => setLabelPl(e.target.value)}
                        onBlur={commitLabelPl}
                        disabled={disabled}
                    />
                </label>
                <label className="lane-cfg-field lane-cfg-field--category">
                    <span className="lane-cfg-field-label">{t("lanes_cfg_category")}</span>
                    <select
                        className="lane-cfg-select"
                        value={config.category}
                        onChange={e => setCategory(e.target.value as StatusCategory)}
                        disabled={disabled}
                    >
                        {CATEGORY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{t(opt.key)}</option>
                        ))}
                    </select>
                </label>
                <fieldset className="lane-cfg-flags" disabled={disabled}>
                    <span className="lane-cfg-field-label">{t("lanes_cfg_applies")}</span>
                    <div className="lane-cfg-flags-row">
                        <label className="lane-cfg-check">
                            <input
                                type="checkbox"
                                checked={config.flags.totes}
                                onChange={() => toggleFlag("totes", "has_totes")}
                            />
                            {t("lanes_cfg_flag_totes")}
                        </label>
                        <label className="lane-cfg-check">
                            <input
                                type="checkbox"
                                checked={config.flags.auctions}
                                onChange={() => toggleFlag("auctions", "has_auctions")}
                            />
                            {t("lanes_cfg_flag_auctions")}
                        </label>
                        <label className="lane-cfg-check">
                            <input
                                type="checkbox"
                                checked={config.flags.auctionEndDate}
                                onChange={() => toggleFlag("auctionEndDate", "has_auction_end_date")}
                            />
                            {t("lanes_cfg_flag_end_date")}
                        </label>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

export default LaneStatusColoursAdmin;
